import { forwardRef, useImperativeHandle, useRef } from 'react';
import { authedFetch } from '../../ai/authedFetch';
import { fetchPollStatus } from '../../utils/pollRetry';
import { capabilityDeniedMessage } from '../../utils/capabilityErrors';
import { readSubmitGrant } from '../../utils/runGrant';
import { createLimiter } from '../../utils/limiter';
import { planMattergenJobs, normalizeMattergenChunkSize, DEFAULT_MATTERGEN_CHUNK_SIZE, MATTERGEN_MAX_PARALLEL } from './mattergenJobs';
// Part 1a: every job body comes from the row's payload (src/ai/rows/rowPayloads.js), never
// from a plan. The checkpoint, properties, guidance and record_trajectories are row params.
import { mattergenJobBody } from '../../ai/rows/rowPayloads';

const POLL_INTERVAL_MS = 1000;

// The readable part of a submit error body's detail: a string as-is, FastAPI's 422
// validation list ([{ loc, msg }]) as "loc: msg" entries joined by "; ", or an
// { error, message } object as "error: message". null when there is nothing to show.
function submitDetailText(d) {
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    const parts = d.map((e) => {
      if (typeof e === 'string') return e;
      if (!e || typeof e.msg !== 'string') return null;
      const loc = Array.isArray(e.loc) ? e.loc.join('.') : '';
      return loc ? `${loc}: ${e.msg}` : e.msg;
    }).filter(Boolean);
    return parts.length > 0 ? parts.join('; ') : null;
  }
  if (d && (d.error || d.message)) return [d.error, d.message].filter(Boolean).join(': ');
  return null;
}

/**
 * H17: surface the server's explanation on a denied submit (the daily-cap 429 and
 * pilot-expiry 403 carry their reason in the body), not just an opaque status code.
 * Handles the FastAPI shapes ({detail: {error, message}}, {detail: 'str'}, and the 422
 * validation list {detail: [{loc, msg}]} that the 0.2 request bounds return) and the
 * llm-proxy relay shape ({ok: false, error: 'str'}).
 */
export function submitErrorMessage(status, j) {
  const denied = capabilityDeniedMessage(j);
  if (denied) return denied;
  const msg = `MatterGen job submit failed: ${status}`;
  const text = submitDetailText(j && (j.detail ?? j.error ?? null));
  return text ? `${msg}: ${text}` : msg;
}

/**
 * Submit a single MatterGen job, poll until done, and return the result.
 * Reports progress via onProgress callback.
 */
async function submitAndPoll({ base, body, chunkCandidates, signal, activeJobIds, onProgress, onPartial, onStatus, authHeaders }) {
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  const hdrs = { 'Content-Type': 'application/json', ...authHeaders };

  const submitResp = await authedFetch(`${base}/jobs`, {
    method: 'POST',
    headers: hdrs,
    body: JSON.stringify(body),
    signal,
  });
  if (!submitResp.ok) {
    let j = null;
    try { j = await submitResp.json(); } catch (_) {}
    throw new Error(submitErrorMessage(submitResp.status, j));
  }
  const submitted = await submitResp.json();
  const { job_id } = submitted;
  // 0.3: what the plan's per-run limit granted this job (the submit JSON, or llm-proxy's
  // X-Spectra-* headers when the proxy tallied); {} when nothing is reported.
  const grant = readSubmitGrant(submitResp, submitted);
  activeJobIds.current.push({ id: job_id, base, authHeaders });

  const onAbort = () => {
    authedFetch(`${base}/jobs/${job_id}`, { method: 'DELETE', headers: authHeaders }).catch(() => {});
  };
  signal?.addEventListener('abort', onAbort);

  try {
    while (true) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

      const statusResp = await fetchPollStatus(() => authedFetch(`${base}/jobs/${job_id}`, { headers: authHeaders }), { signal });
      if (!statusResp.ok) {
        // D4 durability fix (2026-08-20): a genuine 404 here now means the
        // job record truly doesn't exist anywhere (bad id, or purged) --
        // the backend's memory -> disk -> Firestore read-through
        // (models/mattergen-service/persistence.py) already resolves the
        // much more common case, the owning instance recycling mid-job, to
        // a clean "failed" status with an interrupted-job message instead
        // of a poll 404. Distinguishing this from a generic transient poll
        // error (5xx, network blip) gives the user an honest, actionable
        // message instead of a bare status code.
        if (statusResp.status === 404) {
          throw new Error(`MatterGen job ${job_id} not found: it may have expired or the id is invalid`);
        }
        throw new Error(`MatterGen job poll failed: ${statusResp.status}`);
      }
      const status = await statusResp.json();

      if (onStatus && (status.status === 'running' || status.status === 'queued' || status.status === 'dispatched')) {
        const elapsed = status.elapsed_sec;
        const total = status.total_candidates || chunkCandidates;
        const mins = elapsed ? Math.floor(elapsed / 60) : 0;
        const secs = elapsed ? Math.floor(elapsed % 60) : 0;
        const timeStr = elapsed ? ` — ${mins}m ${String(secs).padStart(2, '0')}s` : '';
        // One GPU per job: "queued" waits for a free GPU, "dispatched" is its GPU starting.
        const queueNote = status.status === 'queued' ? ' (waiting for a GPU)'
          : status.status === 'dispatched' ? ' (starting a GPU)' : '';
        onStatus(`MatterGen: generating ${total} candidates${timeStr}${queueNote}`);
      }
      // 2026-10-15: the service parses mattergen-generate's progress bars, so a running job
      // moves the bar instead of sitting at 0 until it lands.
      const fraction = Number(status.progress?.fraction);
      if (onPartial && status.status === 'running' && Number.isFinite(fraction)) onPartial(fraction);

      if (status.status === 'succeeded') {
        const resultResp = await authedFetch(`${base}/jobs/${job_id}/result`, { headers: authHeaders });
        if (!resultResp.ok) throw new Error(`MatterGen result fetch failed: ${resultResp.status}`);
        const result = await resultResp.json();
        // A granted job generated what it was granted; the run row carries the grant.
        if (onProgress) onProgress(grant.granted ?? chunkCandidates);
        return { request: body, response: result, ...grant };
      }
      if (status.status === 'cancelled') {
        throw new DOMException('Job cancelled', 'AbortError');
      }
      if (status.status === 'failed') {
        const resultResp = await authedFetch(`${base}/jobs/${job_id}/result`, { headers: authHeaders }).catch(() => null);
        let msg = 'MatterGen generation failed';
        if (resultResp) {
          try {
            const err = await resultResp.json();
            const d = err?.detail || err;
            // D4 durability fix (2026-08-20): a job the backend's staleness
            // check marks interrupted (owning instance recycled mid-run,
            // models/mattergen-service/persistence.py's
            // INTERRUPTED_MESSAGE) reports its error as a plain string, not
            // the {error, returncode, stderr} shape a generation-command
            // failure uses. The old version only ever read d?.error, so a
            // string detail silently produced a bare "MatterGen generation
            // failed" with the actual, more useful reason dropped.
            if (typeof d === 'string') {
              msg += `: ${d}`;
            } else {
              const parts = [];
              if (d?.error) parts.push(String(d.error));
              if (d?.returncode !== undefined) parts.push(`returncode=${d.returncode}`);
              if (d?.stderr) parts.push(`stderr: ${String(d.stderr).slice(-3500)}`);
              if (parts.length > 0) msg += `: ${parts.join(' | ')}`;
            }
          } catch (_) {}
        }
        throw new Error(msg);
      }
    }
  } finally {
    signal?.removeEventListener('abort', onAbort);
    activeJobIds.current = activeJobIds.current.filter(j => j.id !== job_id);
  }
}

const MattergenRunner = forwardRef(function MattergenRunner({ onStatus, baseUrl, authHeaders }, ref) {
  const activeJobIds = useRef([]);

  useImperativeHandle(ref, () => ({
    // Part 1a: `payload` is mattergenPayloadForRow(row) (src/ai/rows/rowPayloads.js). The
    // runner reads nothing else: checkpoint, properties, guidance, record_trajectories, the
    // count and the chunk size all come from the row. opts = { onProgress, limiter,
    // maxParallel?, signal, baseUrl, authHeaders }.
    async execute(payload, opts) {
      const options = opts || {};
      const p = payload || {};
      if (onStatus) onStatus('Generating with MatterGen...');
      const totalRequested = Math.floor(Number(p.candidates));
      const base = options.baseUrl || baseUrl || process.env.REACT_APP_MATTERGEN_API || 'http://localhost:8010';
      const effAuthHeaders = options.authHeaders !== undefined ? options.authHeaders : authHeaders;

      // 0.7: every job has ONE batch shape (batch_size <= 8), so batches sum exactly to the
      // requested count and no job over-generates. Part 1a: the chunk size is the row's
      // (64 when a payload carries none).
      const chunkSize = Number(p.chunkSize) || DEFAULT_MATTERGEN_CHUNK_SIZE;
      // One GPU per job: spread the variant over the GPU budget, by default the run's
      // parallel budget (what the run limiter allows in flight), so its jobs land on
      // separate GPUs. opts.gpus 1 keeps the plain chunked split.
      const gpus = Number(options.gpus) || Number(options.maxParallel) || MATTERGEN_MAX_PARALLEL;
      const jobs = planMattergenJobs(totalRequested, chunkSize, gpus);
      if (jobs.length === 0) throw new Error('MatterGen: no candidates requested');

      // 0.7: run-wide pool. useToolsRunner passes ONE limiter per run, shared by every
      // variant, so at most MATTERGEN_MAX_PARALLEL (REACT_APP_MATTERGEN_MAX_PARALLEL,
      // default 3) jobs are in flight across the whole run. A caller without one still
      // gets a per-call bound of the same size.
      const limit = typeof options.limiter === 'function'
        ? options.limiter
        : createLimiter(Number(options.maxParallel) || MATTERGEN_MAX_PARALLEL);

      // Progress advances as each job lands, by that job's candidate count.
      // A running job counts its reported fraction of its candidates, floored, until it lands.
      let done = 0;
      const partial = new Map();
      const report = () => {
        if (typeof options.onProgress !== 'function') return;
        let inFlight = 0;
        partial.forEach((n) => { inFlight += n; });
        options.onProgress({ key: 'mattergen', done: Math.min(totalRequested, done + inFlight), total: totalRequested, label: 'inorganic_crystal' });
      };
      const onJobDone = (index) => (jobCandidates) => {
        partial.delete(index);
        done += jobCandidates;
        report();
      };
      const onJobPartial = (index, jobCandidates) => (fraction) => {
        const n = Math.floor(Math.min(Math.max(fraction, 0), 1) * jobCandidates);
        if (n === (partial.get(index) || 0)) return;
        partial.set(index, n);
        report();
      };

      const promises = jobs.map((job, index) => limit(() => submitAndPoll({
        base,
        body: mattergenJobBody(p, job),
        chunkCandidates: job.candidates,
        signal: options.signal,
        activeJobIds,
        onProgress: onJobDone(index),
        onPartial: onJobPartial(index, job.candidates),
        onStatus,
        authHeaders: effAuthHeaders,
      })));

      // 0.7: allSettled per job. A failed job is reported and the rest of the variant is
      // kept; the variant rejects only when every job failed. A cancel on any job still
      // rejects with that real AbortError, so useToolsRunner sees a user cancel.
      const settled = await Promise.allSettled(promises);
      const runs = [];
      const failedJobs = [];
      // Each run names its row (rowId, target, customModelId: Part 1a, so results group and
      // Refine read the row, not the body, which for a weights-mode custom row has no
      // model_path), the chunk size that actually ran and the row's requested total, so
      // Refine can restore both (spec 0.7) instead of one job's batch product.
      const ranChunkSize = normalizeMattergenChunkSize(chunkSize);
      const stamp = { rowId: p.rowId ?? null, target: p.checkpoint ?? null, customModelId: p.customModelId ?? null };
      settled.forEach((s, index) => {
        if (s.status === 'fulfilled') runs.push({ ...stamp, ...s.value, base, chunk_size: ranChunkSize, variant_total: totalRequested });
        else failedJobs.push({ index, candidates: jobs[index].candidates, reason: s.reason });
      });
      const aborted = failedJobs.find((f) => f.reason && f.reason.name === 'AbortError');
      if (aborted) throw aborted.reason;
      if (runs.length === 0) throw failedJobs[0].reason;
      if (failedJobs.length > 0) {
        const lost = failedJobs.reduce((sum, f) => sum + f.candidates, 0);
        console.warn(
          `MatterGen: ${failedJobs.length} of ${jobs.length} jobs failed (${lost} candidates); keeping ${runs.length}.`,
          failedJobs.map((f) => f.reason),
        );
        if (onStatus) onStatus(`MatterGen: ${failedJobs.length} of ${jobs.length} jobs failed; kept the other ${runs.length}`);
      }
      return {
        ok: true,
        total_requested: totalRequested,
        runs,
        failed_jobs: failedJobs.map((f) => ({
          index: f.index,
          candidates: f.candidates,
          message: String((f.reason && f.reason.message) || f.reason),
        })),
      };
    },

    /** Cancel all active MatterGen jobs on the backend (kills GPU subprocesses). */
    cancelAll() {
      for (const j of activeJobIds.current) {
        authedFetch(`${j.base}/jobs/${j.id}`, { method: 'DELETE', headers: j.authHeaders }).catch(() => {});
      }
      activeJobIds.current = [];
    },
  }));
  return null;
});

export default MattergenRunner;
